/**
 * SiteCard: compact summary of one site with a link to its admin page.
 */
import { Link } from 'react-router-dom';
import type { SiteMapEntry } from '../models/siteMapModels';
import {
  entryHealthDetail,
  entryHealthLabel,
  entryTone,
  formatActivityWindow,
  formatShotCount,
  lastShotText,
} from '../utils/siteMapPresentation';
import { SiteMapStatusPill } from './SiteMapStatusPill';

interface SiteCardProps {
  readonly entry: SiteMapEntry;
  readonly now: Date;
  readonly recentShotsWindowS: number;
  /** Query string to keep on navigation, including its leading '?' */
  readonly searchString: string;
  readonly onOpen: (siteId: string) => void;
}

export function SiteCard({
  entry,
  now,
  recentShotsWindowS,
  searchString,
  onOpen,
}: SiteCardProps): JSX.Element {
  const { site, health } = entry;
  const detail = entryHealthDetail(entry);
  const headingId = `site-map-card-${site.siteId}`;

  return (
    <article className="site-map-card" aria-labelledby={headingId}>
      <div className="site-map-card__header">
        <h3 id={headingId} className="site-map-card__name">
          {site.name}
        </h3>
        <span className="site-map-card__id">{site.siteId}</span>
      </div>

      <div className="site-map-card__health">
        <SiteMapStatusPill tone={entryTone(entry)} label={entryHealthLabel(entry)} />
        {detail && <span className="site-map-card__detail">{detail}</span>}
      </div>

      {health && (
        <dl className="site-map-card__facts">
          <div className="site-map-card__fact">
            <dt>Last shot</dt>
            <dd
              title={health.lastShotAt ? new Date(health.lastShotAt).toLocaleString() : undefined}
            >
              {lastShotText(health.lastShotAt, now)}
            </dd>
          </div>
          <div className="site-map-card__fact">
            <dt>Last {formatActivityWindow(recentShotsWindowS)}</dt>
            <dd>{formatShotCount(health.recentShots)}</dd>
          </div>
        </dl>
      )}

      <Link
        to={`/site/${encodeURIComponent(site.siteId)}${searchString}`}
        className="site-map-card__open"
        onClick={() => onOpen(site.siteId)}
      >
        Open site
      </Link>
    </article>
  );
}
